import { useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ExcursionLightboxProps {
  images: string[];
  captions?: string[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

export const ExcursionLightbox = ({
  images,
  captions = [],
  initialIndex = 0,
  isOpen,
  onClose,
}: ExcursionLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    startIndex: initialIndex,
  });

  useEffect(() => {
    setCurrentIndex(initialIndex);
    setZoom(1);
    if (emblaApi) {
      emblaApi.scrollTo(initialIndex);
    }
  }, [initialIndex, isOpen, emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCurrentIndex(emblaApi.selectedScrollSnap());
      setZoom(1);
    };

    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length]);

  const handlePrevious = () => {
    emblaApi?.scrollPrev();
  };

  const handleNext = () => {
    emblaApi?.scrollNext();
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.5, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.5, 1));

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Visionneuse de photos"
      aria-describedby="lightbox-description"
    >
      <div id="lightbox-description" className="sr-only">
        Utilisez les flèches gauche et droite pour naviguer, Échap pour fermer, + et - pour zoomer
      </div>
      {/* Close Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full z-20 focus-visible-ring"
        aria-label="Fermer la visionneuse de photos"
      >
        <X className="w-6 h-6" aria-hidden="true" />
      </Button>

      {/* Image Counter */}
      <div 
        className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium z-20"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        Image {currentIndex + 1} sur {images.length}
      </div>

      {/* Zoom Controls */}
      <div className="absolute top-4 right-20 flex gap-2 z-20" role="group" aria-label="Contrôles de zoom">
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            handleZoomIn();
          }}
          className="text-white bg-black/50 hover:bg-black/70 rounded-full focus-visible-ring"
          aria-label={`Zoom avant - Niveau actuel ${Math.round(zoom * 100)}%`}
          disabled={zoom >= 3}
        >
          <ZoomIn className="w-5 h-5" aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            handleZoomOut();
          }}
          className="text-white bg-black/50 hover:bg-black/70 rounded-full focus-visible-ring"
          aria-label={`Zoom arrière - Niveau actuel ${Math.round(zoom * 100)}%`}
          disabled={zoom <= 1}
        >
          <ZoomOut className="w-5 h-5" aria-hidden="true" />
        </Button>
      </div>

      {/* Navigation Buttons */}
      {images.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full z-10 focus-visible-ring"
            aria-label={`Image précédente - Aller à l'image ${currentIndex === 0 ? images.length : currentIndex}`}
          >
            <ChevronLeft className="w-8 h-8" aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full z-10 focus-visible-ring"
            aria-label={`Image suivante - Aller à l'image ${currentIndex === images.length - 1 ? 1 : currentIndex + 2}`}
          >
            <ChevronRight className="w-8 h-8" aria-hidden="true" />
          </Button>
        </>
      )}

      {/* Main Image - Swipeable */}
      <div 
        className="relative max-w-[90vw] max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {images.map((image, index) => (
              <div 
                key={index}
                className="flex-[0_0_100%] min-w-0 flex items-center justify-center"
              >
                <img
                  src={image}
                  alt={captions[index] || `Photo ${index + 1}`}
                  className="max-w-full max-h-[90vh] object-contain transition-transform duration-200"
                  style={{ transform: `scale(${zoom})` }}
                  loading={Math.abs(index - currentIndex) <= 1 ? 'eager' : 'lazy'}
                />
                {captions[index] && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                    <p className="text-white text-center">{captions[index]}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div 
          className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-4" 
          role="tablist" 
          aria-label="Miniatures des photos"
        >
          {images.map((image, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                emblaApi?.scrollTo(index);
              }}
              className={cn(
                "flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all focus-visible-ring",
                currentIndex === index 
                  ? "border-white scale-110" 
                  : "border-transparent opacity-60 hover:opacity-100"
              )}
              role="tab"
              aria-label={`Aller à la photo ${index + 1}${captions[index] ? ` - ${captions[index]}` : ''}`}
              aria-selected={currentIndex === index}
              aria-current={currentIndex === index ? 'true' : undefined}
            >
              <img
                src={image}
                alt={`Miniature photo ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
